import React from 'react';

import CountertopsOutlinedIcon from '@mui/icons-material/CountertopsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ExposureOutlinedIcon from '@mui/icons-material/ExposureOutlined';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';

import { toKebabCase } from '../../../../helpers/strings';
import { SettingsMenuItem } from '../../../../ui/List/SettingsList';
import { RMSModules } from '../../utils/rmsModules';

// Menu item templates to reduce object creation
const MENU_ITEM_TEMPLATES: Record<string, SettingsMenuItem> = {
  ORGANIZATION_SETTINGS: {
    title: 'Organisation_settings',
    path: '/settings',
    icon: <SettingsOutlinedIcon />,
    subtitle: 'Configure_organisation_settings',
    searchAlias: ['Organisation settings', 'Organisation configuration'],
  },
  CHARGES_CONFIGURATION: {
    title: 'Charges_Configuration',
    path: `/${toKebabCase(RMSModules.CHARGES_CONFIGURATION)}`,
    icon: <CreditCardOutlinedIcon />,
    subtitle: 'Configure_charges',
    searchAlias: ['Charges configuration'],
  },
  LOCALIZATION_CONFIGURATION: {
    title: 'Localization_Configuration',
    path: `/${toKebabCase(RMSModules.LOCALIZATION_CONFIGURATION)}`,
    icon: <LanguageOutlinedIcon />,
    subtitle: 'Configure_localization_setting_overrides',
    searchAlias: ['Localization configuration'],
  },
  OPERATING_CONFIGURATION: {
    title: 'Operating_Configuration',
    path: `/${toKebabCase(RMSModules.OPERATING_CONFIGURATION)}`,
    icon: <SettingsApplicationsOutlinedIcon />,
    subtitle: 'Configure_operating_configuration',
    searchAlias: ['Operating configuration'],
  },
  KITCHEN_STATION_PROFILES: {
    title: 'Kitchen_Station_Profiles',
    path: `/${toKebabCase(RMSModules.KITCHEN_STATION_PROFILES)}`,
    icon: <CountertopsOutlinedIcon />,
    subtitle: 'Manage_kitchen_station_profiles',
    searchAlias: ['Kitchen station profiles'],
  },
  TERMINOLOGY: {
    title: 'Terminology',
    path: '/terminology',
    icon: <MenuBookOutlinedIcon />,
    subtitle: 'Manage_terminology',
    searchAlias: ['Terminology'],
  },
  LICENSE: {
    title: 'License',
    path: `/${toKebabCase(RMSModules.LICENSE)}`,
    icon: <ExposureOutlinedIcon />,
    subtitle: 'Manage_property_level_license_subscriptions',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
    searchAlias: ['License'],
  },
  API_INTEGRATION: {
    title: 'API_Integration',
    path: `/${toKebabCase(RMSModules.API_INTEGRATIONS)}`,
    icon: <IntegrationInstructionsOutlinedIcon />,
    subtitle: 'Configure_API_integration',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
    searchAlias: ['API integration'],
  },
  DB_CONFIGURATION: {
    title: 'DB_Configuration',
    path: `/${toKebabCase(RMSModules.DB_CONFIGURATION)}`,
    icon: <StorageOutlinedIcon />,
    subtitle: 'Configure_database_configuration',
    tagText: 'Flipdish_staff_only',
    permissions: ['FlipdishStaff'],
    searchAlias: ['Database configuration', 'DB configuration'],
  },
};

// Helper function to create menu items with common data
const createMenuItem = (template: SettingsMenuItem, baseUrl: string): SettingsMenuItem => ({
  ...template,
  path: `${baseUrl}${template.path}`,
});

export const getOrganizationMenuItems = ({
  baseUrl,
  permissions,
}: {
  baseUrl: string;
  permissions: string[];
}) => {
  const menuItems: SettingsMenuItem[] = [];

  // Always include these items
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.ORGANIZATION_SETTINGS, baseUrl));
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.CHARGES_CONFIGURATION, baseUrl));
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.LOCALIZATION_CONFIGURATION, baseUrl));
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.OPERATING_CONFIGURATION, baseUrl));
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.KITCHEN_STATION_PROFILES, baseUrl));
  menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.TERMINOLOGY, baseUrl));

  // Flipdish staff only items
  if (permissions.includes('FlipdishStaff')) {
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.LICENSE, baseUrl));
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.API_INTEGRATION, baseUrl));
    menuItems.push(createMenuItem(MENU_ITEM_TEMPLATES.DB_CONFIGURATION, baseUrl));
  }

  return menuItems;
};
